import * as cx from 'classnames';
import * as moment from 'moment';
import * as _ from 'lodash';
import BranchDropdown from './BranchDropdown';
import { Icon, IconText, Link, icons } from '../../generic';
import styles from './HybridProjectRow.module.less';
import type { OnBranchChangeFn, OnRowRedirectFn, OnSelectRepositoryFn } from './ServiceImportRepositories';
import type { Repository } from '../../../types';

type HybridProjectRowProps = {
  repo: Repository;
  selectedRepos: Repository[];
  onBranchChange: OnBranchChangeFn;
  onRowRedirect: OnRowRedirectFn;
  onSelectRepository: OnSelectRepositoryFn;
};

const HybridProjectRow = ({
  repo,
  selectedRepos,
  onBranchChange,
  onRowRedirect,
  onSelectRepository,
}: HybridProjectRowProps) => {
  const isImported = !!repo.project;
  const hasNoBranches = !repo.branches || _.isEmpty(repo.branches);
  const isSelected = !!selectedRepos.find((selectedRepo: Repository) => selectedRepo.title === repo.title);
  const isConnectable = (repo.connectableProjects ?? []).length > 0;
  // eslint-disable-next-line no-nested-ternary
  const repoStatus = isImported ? 'Imported' : isConnectable ? 'Connectable' : 'Ready to Import';

  const project = repo.project;
  let onRowClick = _.noop;
  if (isImported) {
    onRowClick = () => onRowRedirect(project?.locator ?? '');
  } else if (hasNoBranches) {
    onRowClick = _.noop;
  } else {
    onRowClick = () => onSelectRepository(repo);
  }
  const rowClassNames = cx(styles.row, {
    [styles.selected]: isSelected,
    [styles.success]: isImported,
    [styles.disabled]: hasNoBranches,
  });

  // There are a couple of linter workarounds here, but this is short-term temoprary code so fear not.
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={rowClassNames}
      onClick={(e) => {
        // Opening the branch dropdown calls e.preventDefault(). We
        // catch that here to avoid selecting the current repository.
        if (e.defaultPrevented) {
          return;
        }

        onRowClick();
      }}
    >
      <span className="text-center">
        {
          // eslint-disable-next-line no-nested-ternary
          hasNoBranches ? (
            <span className={styles.noBranches} />
          ) : (
            isImported ? (
              <Icon icon={icons.externalLink} />
            ) : (
              <input type="checkbox" checked={isSelected} />
            )
          )
}
      </span>
      <span>
        <span className="margin-right-xs">
          {repo.isFork && <Icon icon={icons.codeFork} />}
        </span>
        <Link
          to={repo.url as string}
          openInNewTab
        >
          <b>{repo.title}</b>
        </Link>
          &nbsp;&nbsp;
        {repo.isPrivate
          && (
            <span className="badge">
              <IconText
                icon={icons.lock}
                text="Private"
              />
            </span>
          )}
      </span>
      <span>
        {isImported
          ? (
            <>
              <small>
                <IconText
                  icon={icons.checkCircle}
                  text={`Imported: ${moment(project?.createdAt).fromNow()}`}
              />
              </small>
              <span>{!hasNoBranches && repo.branch}</span>
            </>
            ) : (
              !hasNoBranches && <BranchDropdown onBranchChange={onBranchChange} repo={repo} />
            )}
      </span>
      <span>
        {repo.updated ? <i>({moment(repo.updated).fromNow()})</i> : ''}
      </span>
      <span>
        {repoStatus}
      </span>
    </div>
  );
};

export default HybridProjectRow;
